/** Shared helpers for Feed Management UI (status, labels, feed-type colors). */

/** @deprecated Nested feed tabs removed in field-edition 5b-2 (single-page Feed Tracker). */
export const FEED_MGMT_TABS = [
  { id: 'stands', label: 'Feeders' },
  { id: 'inventory', label: 'Inventory' },
  { id: 'stores', label: 'Feed Stores' },
  { id: 'outlook', label: 'Consumption Outlook' },
];

/** Infer hopper capacity when overview omits feed_capacity_lbs. */
export function estimateCapacityLbs(remainingLbs, percentRemaining) {
  const remaining = Number(remainingLbs);
  const pct = Number(percentRemaining);
  if (!Number.isFinite(remaining) || remaining < 0) return null;
  if (!Number.isFinite(pct) || pct <= 0) return null;
  return Math.max(remaining, Math.round(remaining / (pct / 100)));
}

/** Whole days until empty from rate, optionally cross-checked with refill due date. */
export function estimateDaysLeft(remainingLbs, rateLbsPerDay, refillDueDate) {
  const remaining = Number(remainingLbs);
  const rate = Number(rateLbsPerDay);
  if (Number.isFinite(remaining) && Number.isFinite(rate) && rate > 0) {
    return Math.max(0, Math.ceil(remaining / rate));
  }
  if (!refillDueDate) return null;
  try {
    const due = new Date(refillDueDate);
    if (Number.isNaN(due.getTime())) return null;
    const diff = Math.ceil((due.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
    return Number.isFinite(diff) ? Math.max(0, diff) : null;
  } catch (_) {
    return null;
  }
}

/**
 * 0..dayCount projected fill heights (percent of capacity) for draw-down spark bars.
 * @returns {{ day: number, heightPct: number, urgency: 'good'|'warn'|'danger' }[]}
 */
export function buildDrawdownBars({
  remainingLbs = 0,
  rateLbsPerDay = 0,
  capacityLbs = null,
  dayCount = 14,
} = {}) {
  const remaining = Math.max(0, Number(remainingLbs) || 0);
  const rate = Math.max(0, Number(rateLbsPerDay) || 0);
  const capacity =
    Number.isFinite(Number(capacityLbs)) && Number(capacityLbs) > 0
      ? Number(capacityLbs)
      : Math.max(remaining, 1);
  const bars = [];
  let level = remaining;
  for (let day = 0; day <= dayCount; day += 1) {
    const heightPct = Math.max(0, Math.min(100, (level / capacity) * 100));
    let urgency = 'good';
    if (heightPct <= 0) urgency = 'danger';
    else if (heightPct < 20) urgency = 'warn';
    bars.push({ day, heightPct, urgency });
    level = Math.max(0, level - rate);
  }
  return bars;
}

/** Season feed need for active stations (lbs) over `days` at current rates. */
export function estimateSeasonFeedNeedLbs(targets = [], days = 90) {
  const list = Array.isArray(targets) ? targets : [];
  const daily = list.reduce((sum, t) => sum + (Number(t?.consumptionRateLbsPerDay) || 0), 0);
  return Math.round(daily * days);
}

/**
 * Fill bar percent (0–100). Prefer API percent; else remaining/capacity;
 * empty when remaining is 0.
 */
export function resolveStationFillPercent({
  estimatedRemaining = 0,
  percentRemaining = null,
  capacityLbs = null,
} = {}) {
  const remaining = Math.max(0, Number(estimatedRemaining) || 0);
  if (percentRemaining != null && Number.isFinite(Number(percentRemaining))) {
    return Math.max(0, Math.min(100, Number(percentRemaining)));
  }
  const capacity = Number(capacityLbs);
  if (Number.isFinite(capacity) && capacity > 0) {
    return Math.max(0, Math.min(100, (remaining / capacity) * 100));
  }
  return remaining > 0 ? 100 : 0;
}

/** Same codes/labels as map marker & feature dialogs (HuntLocation / GeoFeature). */
export const FEED_CONSUMPTION_RATE_OPTIONS = [
  { value: 'low', label: 'Low (≈5 lbs/day)' },
  { value: 'avg', label: 'Average (≈10 lbs/day)' },
  { value: 'high', label: 'High (≈20 lbs/day)' },
  { value: 'very_high', label: 'Very high (≈30 lbs/day)' },
];

const RATE_LBS_BY_CODE = {
  low: 5,
  avg: 10,
  high: 20,
  very_high: 30,
};

export function getConsumptionRateLbsPerDay(code, rateMap = RATE_LBS_BY_CODE) {
  const key = (code || '').toString().trim().toLowerCase();
  const numeric = Number(rateMap[key]);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : RATE_LBS_BY_CODE[key] || 0;
}

export function getConsumptionRateOptionLabel(code) {
  const key = (code || '').toString().trim().toLowerCase();
  const match = FEED_CONSUMPTION_RATE_OPTIONS.find((opt) => opt.value === key);
  return match?.label || '';
}

export function getFeedingModeLabel(mode) {
  const key = (mode || '').toString().trim().toLowerCase();
  if (key === 'hopper') return 'Hopper';
  if (key === 'ground') return 'Ground';
  return '';
}

/** Stand/feeder card sort keys (Feed Management → Feeders tab). */
export const FEED_STAND_SORT_OPTIONS = [
  { value: 'name', label: 'Name (A–Z)' },
  { value: 'remaining-asc', label: 'Lbs remaining (low → high)' },
  { value: 'remaining-desc', label: 'Lbs remaining (high → low)' },
];

function compareFeedTargetNames(a, b) {
  const aName = (a?.name || '').toLowerCase();
  const bName = (b?.name || '').toLowerCase();
  if (aName === bName) return 0;
  return aName < bName ? -1 : 1;
}

/** Sort mapped feed targets for display (non-mutating). */
export function sortFeedTargets(targets = [], sortKey = 'name') {
  const list = Array.isArray(targets) ? [...targets] : [];
  if (sortKey === 'remaining-asc') {
    return list.sort((a, b) => {
      const diff = (Number(a?.estimatedRemaining) || 0) - (Number(b?.estimatedRemaining) || 0);
      return diff !== 0 ? diff : compareFeedTargetNames(a, b);
    });
  }
  if (sortKey === 'remaining-desc') {
    return list.sort((a, b) => {
      const diff = (Number(b?.estimatedRemaining) || 0) - (Number(a?.estimatedRemaining) || 0);
      return diff !== 0 ? diff : compareFeedTargetNames(a, b);
    });
  }
  return list.sort(compareFeedTargetNames);
}

export function getFeedLevelStatus(percentRemaining) {
  if (percentRemaining == null || Number.isNaN(Number(percentRemaining))) return 'unknown';
  const pct = Number(percentRemaining);
  if (pct <= 0) return 'empty';
  if (pct < 20) return 'low';
  return 'good';
}

export function getFeedStatusLabel(status) {
  switch (status) {
    case 'good':
      return 'Good';
    case 'low':
      return 'Low';
    case 'empty':
      return 'Empty';
    default:
      return 'Unknown';
  }
}

export function normalizeFeedTypeKey(value) {
  return (value || '').toString().trim().toLowerCase();
}

/** CSS custom property or token-friendly color per feed type (mockup dot colors). */
export function getFeedTypeDotColor(feedType) {
  const key = normalizeFeedTypeKey(feedType);
  if (key.includes('corn') && !key.includes('mix')) return 'var(--color-warning)';
  if (key.includes('protein')) return 'var(--color-success)';
  if (key.includes('mineral')) return 'var(--chart-series-3, #3b82f6)';
  if (key.includes('rice')) return 'var(--color-brand-accent)';
  if (key.includes('soy')) return 'var(--chart-series-2, #84cc16)';
  if (key.includes('oat')) return '#ca8a04';
  if (key.includes('mix') || key.includes('blend')) return '#a855f7';
  return 'var(--color-text-muted)';
}

/** Club hunt location vs personal map GeoFeature (from /feeding/overview/). */
export function isClubFeeder(item) {
  if (!item) return false;
  if (item.kind === 'club') return true;
  return item.club_id != null && item.club_id !== '';
}

/** Personal Toolkit Feed Tracker source list. Club hoppers stay on the club map. */
export function personalFeedStations(items = []) {
  return (Array.isArray(items) ? items : []).filter((item) => !isClubFeeder(item));
}

export function buildClubNameMap(clubs = []) {
  const map = new Map();
  (clubs || []).forEach((club) => {
    if (club?.id != null) map.set(club.id, club.name || 'Club');
  });
  return map;
}

function lookupClubName(clubId, clubNameById) {
  if (clubId == null || clubId === '') return null;
  const numericId = Number(clubId);
  return (
    clubNameById.get(clubId) ||
    (Number.isFinite(numericId) ? clubNameById.get(numericId) : null) ||
    clubNameById.get(String(clubId)) ||
    null
  );
}

export function resolveFeederScopeLabel(item, clubNameById = new Map()) {
  if (!isClubFeeder(item)) return 'Personal';
  const clubName =
    item?.club_name ||
    lookupClubName(item?.club_id, clubNameById) ||
    'Club';
  return `Club · ${clubName}`;
}

/** Overview / mapped target is actively tracked (false → greyed out, excluded from metrics). */
export function isFeederActive(item) {
  return item?.is_active !== false && item?.isActive !== false;
}

/** Per-event feed type from API; falls back to stand default only when the event has none. */
export function resolveFeedEventFeedType(event, targetFeedType = '') {
  const fromEvent = (event?.feed_type ?? '').toString().trim();
  if (fromEvent) return fromEvent.toLowerCase();
  return (targetFeedType || '').toString().trim().toLowerCase();
}

export function formatFeedTypeLabel(feedType) {
  const raw = (feedType || '').toString().trim();
  if (!raw) return 'Feed';
  return raw
    .split(/[\s_/]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function countFeedersNeedingAttention(refills = []) {
  if (!Array.isArray(refills)) return 0;
  return refills.filter((item) => {
    if (!isFeederActive(item)) return false;
    const pct = item?.percent_remaining;
    if (pct == null) return false;
    return Number(pct) < 20;
  }).length;
}

export function buildUrgentRefillTasks(refills = []) {
  if (!Array.isArray(refills)) return [];
  return refills
    .filter((item) => {
      if (!isFeederActive(item)) return false;
      const pct = item?.percent_remaining;
      return pct != null && Number(pct) < 20;
    })
    .map((item) => {
      const pct = Number(item.percent_remaining);
      const priority = pct <= 0 ? 'critical' : 'high';
      const feedLabel = formatFeedTypeLabel(item.default_feed_type || item.feed_type);
      const task =
        pct <= 0
          ? `Refill ${feedLabel} (EMPTY)`
          : `Refill ${feedLabel} (LOW)`;
      return {
        id: `${item.kind}-${item.id}`,
        stand: item.name || 'Feeder',
        task,
        dueDate: item.refill_due_date || 'Soon',
        priority,
        target: { kind: item.kind, id: item.id },
      };
    })
    .sort((a, b) => {
      const order = { critical: 0, high: 1, medium: 2, low: 3 };
      return (order[a.priority] ?? 9) - (order[b.priority] ?? 9);
    });
}
